import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useNavigate, useSearchParams, useLocation } from "react-router-dom"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Card, CardContent } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import {
  Briefcase,
  Calendar,
  Search,
  Filter,
  MapPin,
  Clock,
  ArrowRight,
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Eye,
  Mail,
  Phone,
  MoreHorizontal,
  RefreshCw,
  UserCheck,
  Building,
  User,
  CheckCircle,
  Download,
  Pencil,
  MoreVertical,
} from "lucide-react"
import { toast } from "react-hot-toast"
import { getCoordinatedJobs, getJobDetailsById, getJobs } from "../../services/jobsapi"
import {
  getAllApplications,
  getCoordinatedinterviews,
  updateApplicationStatus,
  reassignCandidateApplication,
  updateJoiningDate,
  updateCandidateContactDetails,
  editApplication,
} from "../../services/candidateapi"
import { getAllUsers, getMySubordinates } from "../../services/usersapi"
import { exportApplicationsExcel, exportCoordinatedInterviewsExcel } from "../../services/apiclient"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { Label } from "../../components/ui/label"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip"
import { Textarea } from "../../components/ui/textarea"
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select"
import { 
  getInterviewDetailsByApplicationId,
  scheduleInterviewRound,
  rescheduleInterviewRound,
  updateInterviewRoundStatus,
  type InterviewDetailsResponse,
} from "../../services/interviewapi"
import { useRbac } from "../../context"
import { cn } from "../../components/lib/utils"
import { InterviewDetailsModal } from "../../components/InterviewDetailsModal"
import { ResultModal } from "../../components/ResultModal"
import type {
  Application,
  ScheduleFormData,
  ResultFormData,
  ValidationErrors,
  UserType,
} from "../../components/types/application.types"
import { showConfirmation, getStatusChangeOptions, getDefaultStatusChangeValue, isNotSelectedOnlyStatusChange, APPLICATION_STATUS_FILTER_OPTIONS, isInterviewTodayStatusFilter } from "../../components/utils/application.utils"
import { format } from "date-fns"

const CHANGE_COMPANY_BLOCKED_STATUSES: string[] = []

interface Job {
  id: string
  title: string
  companyName: string
  companyLogoUrl?: string
  location: string
  employmentType: string
  workType: string
  status: string
  noOfOpenings: number
  createdAt: string
  isArchived?: boolean
  assignedCount?: number
  assignedUserCount?: number
  isExternal?: boolean
  eligibleForCampusDrive?: boolean
  link?: string
}

const getJobCompanyName = (job: { companyName?: string; companyId?: { name?: string } }): string =>
  (job.companyName || job.companyId?.name || "").trim()

const CHANGE_STATUS_ENABLED_STATUSES = ["Selected", "Offered", "Interview Attended"]

const CoordinationsPage = () => {
  const { user } = useRbac()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Initialize activeTab from URL query params, default to "jobs"
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get("tab")
    // Validate tab value - only allow "jobs" or "interviews"
    if (tabFromUrl === "interviews" || tabFromUrl === "jobs") {
      return tabFromUrl
    }
    return "jobs" // Default to "jobs" if no valid tab in URL
  })
  
  // Update URL when activeTab changes
  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab(newTab)
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set("tab", newTab)
    setSearchParams(newSearchParams, { replace: true })
  }, [searchParams, setSearchParams])
  
  // Sync activeTab with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab")
    // Validate tab value - only allow "jobs" or "interviews"
    const urlTab = (tabFromUrl === "interviews" || tabFromUrl === "jobs") ? tabFromUrl : "jobs"
    if (urlTab !== activeTab) {
      setActiveTab(urlTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
  
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("published")
  // Initialize currentPage from location.state if available (for navigation back)
  const [currentPage, setCurrentPage] = useState(() => {
    const state = location.state as { pageNumber?: number; selectedSHM?: string } | null
    return state?.pageNumber || 1
  })
  const [totalPages, setTotalPages] = useState(1)
  const [totalResults, setTotalResults] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('coordinationsItemsPerPage')
    return saved ? Number(saved) : 12
  })
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [showInterviewFilters, setShowInterviewFilters] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const isFetchingRef = useRef(false)
  const hasRestoredStateRef = useRef(false)
  const restoredPageRef = useRef<number | null>(null)
  const isInitialMountRef = useRef(true)
  
  // Interview state
  const [interviews, setInterviews] = useState<Application[]>([])
  const [interviewsLoading, setInterviewsLoading] = useState(false)
  const [interviewsPage, setInterviewsPage] = useState(1)
  const [interviewsTotalPages, setInterviewsTotalPages] = useState(1)
  const [interviewsTotalResults, setInterviewsTotalResults] = useState(0)
  const [interviewsItemsPerPage, setInterviewsItemsPerPage] = useState(20)
  const [interviewDetailsMap, setInterviewDetailsMap] = useState<Record<string, any>>({})
  
  // Interview filter state
  const [interviewsSearchTerm, setInterviewsSearchTerm] = useState("")
  const [selectedJob, setSelectedJob] = useState("all")
  const [selectedCompany, setSelectedCompany] = useState("all")
  const [selectedStatus, setSelectedStatus] = useState("all")
  const [selectedRecruiter, setSelectedRecruiter] = useState("all")
  const [selectedSubordinate, setSelectedSubordinate] = useState("all")
  const [selectedSHM, setSelectedSHM] = useState("all")
  const [selectedDateFrom, setSelectedDateFrom] = useState("")
  const [selectedDateTo, setSelectedDateTo] = useState("")
  
  // Filter data state
  const [filterJobs, setFilterJobs] = useState<any[]>([])
  const [recruiters, setRecruiters] = useState<UserType[]>([])
  const [subordinates, setSubordinates] = useState<UserType[]>([])
  const [shmUsers, setShmUsers] = useState<UserType[]>([])
  const [isLoadingJobs, setIsLoadingJobs] = useState(false)
  const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false)
  const [isLoadingSubordinates, setIsLoadingSubordinates] = useState(false)
  const [isLoadingSHM, setIsLoadingSHM] = useState(false)
  const [jobSearchTerm, setJobSearchTerm] = useState("")
  
  // Interview modal state (matching ViewApplications)
  const [showInterviewModal, setShowInterviewModal] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null)
  const [interviewDetails, setInterviewDetails] = useState<InterviewDetailsResponse | null>(null)
  const [showLoaderInInterviewPopup, setShowLoaderInInterviewPopup] = useState(false)
  const [showScheduleForm, setShowScheduleForm] = useState(false)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [showResultModal, setShowResultModal] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)
  const [isSubmittingResult, setIsSubmittingResult] = useState(false)
  const [currentRoundForAction, setCurrentRoundForAction] = useState<any>(null)
  const [scheduleFormData, setScheduleFormData] = useState<ScheduleFormData>({
    interviewType: "Offline",
    scheduledDate: "",
    startTime: "",
    endTime: "",
    timeZone: "UTC",
    interviewers: [],
    notes: "",
    additionalParticipants: [],
    location: "",
  })
  const [resultFormData, setResultFormData] = useState<ResultFormData>({
    result: "Qualified",
    reason: "",
    isFinalRound: false,
    expectedJoiningDate: "",
  })
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    startTime: false,
    endTime: false,
    scheduledDate: false,
  })
  const [emailInput, setEmailInput] = useState("")
  
  // Status update modal state
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false)
  const [showJoiningDateUpdateModal, setShowJoiningDateUpdateModal] = useState(false)
  const [showDateSelect, setShowDateSelect] = useState(false)
  const [joiningDate, setJoiningDate] = useState(new Date().toISOString().split("T")[0])
  const [statusUpdateData, setStatusUpdateData] = useState<{
    applicationId: string
    newStatus: string
    comments: string
    selectedJobId?: string
    currentStatus?: string
  }>({
    applicationId: "",
    newStatus: "",
    comments: "",
    selectedJobId: "",
    currentStatus: "",
  })
  const [isReassigning, setIsReassigning] = useState(false)

  const [showEditCandidateModal, setShowEditCandidateModal] = useState(false)
  const [editCandidateApp, setEditCandidateApp] = useState<Application | null>(null)
  const [editCandidateForm, setEditCandidateForm] = useState({ name: "", email: "", phone: "" })
  const [editCandidateFieldErrors, setEditCandidateFieldErrors] = useState({
    name: "",
    email: "",
    phone: "",
  })
  const [editCandidateSaving, setEditCandidateSaving] = useState(false)
  const [showChangeCompanyModal, setShowChangeCompanyModal] = useState(false)
  const [changeCompanyData, setChangeCompanyData] = useState({
    applicationId: "",
    selectedJobId: "",
    selectedJobLabel: "",
    comments: "",
    candidateName: "",
    currentCompany: "",
  })
  const [isChangingCompany, setIsChangingCompany] = useState(false)
  const [changeCompanyJobs, setChangeCompanyJobs] = useState<any[]>([])
  const [isLoadingChangeCompanyJobs, setIsLoadingChangeCompanyJobs] = useState(false)

 useEffect(() => {
  if (!showStatusUpdateModal && !showJoiningDateUpdateModal && !showEditCandidateModal && !showChangeCompanyModal) {
    setTimeout(() => {
      document.body.style.removeProperty("pointer-events");
      document.body.removeAttribute("data-scroll-locked");
    }, 0);
  }
 }, [showStatusUpdateModal, showJoiningDateUpdateModal, showEditCandidateModal, showChangeCompanyModal])

  const fetchJobs = useCallback(async (page = 1, selectedSHM = "all") => {
    if (isFetchingRef.current) {
      return
    }
    
    try {
      isFetchingRef.current = true
      setLoading(true)
      setIsLoading(true)
      const params: any = {
        page,
        limit: itemsPerPage,
        _t: Date.now(),
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim()
      }

      if (statusFilter !== "all") {
        params.status = statusFilter
      }
      if (selectedSHM !== "all") {
        params.userId = selectedSHM
      }
      

      const response = await getCoordinatedJobs(params)
      console.log("response::=>", response);
      if (response.status === "success" && response.data) {
        setJobs(response.data.jobs)
        setCurrentPage(response.data.page)
        setTotalPages(response.data.totalPages)
        setTotalResults(response.data.totalResults)
      } else {
        setJobs([])
        toast.error("Invalid response from server. Please try again.")
      }
    } catch (error: any) {
      console.error("Failed to fetch jobs:", error)
      setJobs([])
      
      if (error.response?.status === 400) {
        toast.error("Invalid pagination parameters. Please refresh the page.")
      } else if (error.response?.status === 401) {
        toast.error("Authentication failed. Please log in again.")
      } else if (error.response?.status === 403) {
        toast.error("Access denied. You don't have permission to view these jobs.")
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.")
      } else {
        toast.error("Failed to load jobs. Please try again.")
      }
    } finally {
      setLoading(false)
      setIsLoading(false)
      isFetchingRef.current = false
    }
  }, [itemsPerPage, searchTerm, statusFilter])

  // Role-based filter configuration
  const allowedRecruiterRoles = useMemo(() => {
    if (user?.role === "ADMIN") return ["FBDE", "CONSULTANT", "SHM"]
    if (user?.role === "SHM") return ["HM", "AGENCY", "HR"]
    if (user?.role === "FBDE" || user?.role === "CONSULTANT") return ["HM", "AGENCY", "HR"]
    if (user?.role === "HM" || user?.role === "AGENCY") return ["HR"]
    return []
  }, [user?.role])

  // Recruiter options
  const recruiterOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Recruiters" }]
    if (recruiters.length > 0) {
      recruiters
        .filter((recruiter) => allowedRecruiterRoles.includes(recruiter.role))
        .forEach((recruiter) => {
          const username = recruiter.username
          options.push({
            value: recruiter.id,
            label: (
              <div className="text-left">
                <div>{username} <span className="text-sm text-gray-500">{recruiter.role}</span></div>
              </div>
            ),
            searchText: `${username} ${recruiter.email}`.toLowerCase(),
          })
        })
    } else if (!isLoadingRecruiters) {
      options.push({ value: "no-recruiters", label: "No recruiters available", disabled: true })
    }
    return options
  }, [recruiters, isLoadingRecruiters, allowedRecruiterRoles])

  const companyOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Companies" }]
    const uniqueCompanyNames = Array.from(
      new Set(
        filterJobs
          .map((job) => getJobCompanyName(job))
          .filter((name) => Boolean(name) && name !== "N/A")
      )
    ).sort((a, b) => a.localeCompare(b))

    if (uniqueCompanyNames.length > 0) {
      uniqueCompanyNames.forEach((companyName) => {
        options.push({ value: companyName, label: companyName })
      })
    } else if (!isLoadingJobs) {
      options.push({ value: "no-companies", label: "No companies available", disabled: true })
    }
    return options
  }, [filterJobs, isLoadingJobs])

  const jobFilterOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Jobs" }]
    const jobsToShow =
      selectedCompany !== "all"
        ? filterJobs.filter((job) => getJobCompanyName(job) === selectedCompany)
        : filterJobs

    if (jobsToShow.length > 0) {
      jobsToShow.forEach((job) => {
        const companyName = getJobCompanyName(job)
        options.push({
          value: job.id,
          label: `${job.title} — ${companyName || "N/A"} · ${job?.location || "N/A"}`,
          searchText: `${job.title} ${companyName} ${job?.location}`,
        })
      })
    } else if (!isLoadingJobs) {
      options.push({ value: "no-jobs", label: "No jobs available", disabled: true })
    }
    return options
  }, [filterJobs, isLoadingJobs, selectedCompany])

  // Fetch assigned coordinated jobs for interview filters
  const fetchFilterJobs = useCallback(async (searchValue?: string) => {
    if (user?.role === "ADMIN" && selectedSHM === "all") {
      setFilterJobs([])
      return
    }

    setIsLoadingJobs(true)
    try {
      const params: {
        page: number
        limit: number
        search?: string
        userId?: string
      } = {
        page: 1,
        limit: 500,
      }

      if (searchValue?.trim()) {
        params.search = searchValue.trim()
      }

      if (user?.role === "ADMIN" && selectedSHM !== "all") {
        params.userId = selectedSHM
      }

      const response = await getCoordinatedJobs(params)

      if (response?.status === "success" && response?.data?.jobs) {
        setFilterJobs(
          response.data.jobs.map((job: any) => ({
            ...job,
            companyName: getJobCompanyName(job),
          }))
        )
      } else {
        setFilterJobs([])
      }
    } catch (err: any) {
      console.error("Failed to fetch filter jobs:", err)
      setFilterJobs([])
    } finally {
      setIsLoadingJobs(false)
    }
  }, [user?.role, selectedSHM])

  // Fetch recruiters
  const fetchRecruiters = useCallback(async (value?: string) => {
    setIsLoadingRecruiters(true)
    try {
      let response = user?.role === "ADMIN" ? await getAllUsers({role: "FBDE", search: value}) : await getMySubordinates()
      if (user?.role === "ADMIN") {
        const response2 = await getAllUsers({role: "SHM", search: value})
        const response3 = await getAllUsers({role: "CONSULTANT", search: value})
        response.data.users = response.data.users.concat(response2.data.users).concat(response3.data.users)
      }
      if (response?.data?.subordinates) {
        setRecruiters(response.data.subordinates.filter((u: UserType) => allowedRecruiterRoles.includes(u.role)))
      } else if (response?.data?.users) {
        setRecruiters(response.data.users.filter((u: UserType) => allowedRecruiterRoles.includes(u.role)))
      } else {
        setRecruiters([])
      }
    } catch (err: any) {
      console.error("Failed to fetch recruiters:", err)
      setRecruiters([])
    } finally {
      setIsLoadingRecruiters(false)
    }
  }, [allowedRecruiterRoles, user?.role])

  // Fetch subordinates
  const fetchSubordinates = useCallback(async () => {
    setIsLoadingSubordinates(true)
    try {
      const response = await getMySubordinates()
      if (response?.data?.subordinates) {
        setSubordinates(response.data.subordinates)
      } else {
        setSubordinates([])
      }
    } catch (err: any) {
      console.error("Failed to fetch subordinates:", err)
      setSubordinates([])
    } finally {
      setIsLoadingSubordinates(false)
    }
  }, [])

  // Fetch SHM/HM users (for ADMIN only)
  const fetchSHMs = useCallback(async (value?: string) => {
    setIsLoadingSHM(true)
    try {
      const response = await getAllUsers({ role: ["SHM","HM","AGENCY"], search: value,})
      if (response?.data?.users) {
        setShmUsers(response.data.users)
      } else {
        setShmUsers([])
      }
    } catch (err: any) {
      console.error("Failed to fetch SHMs:", err)
      setShmUsers([])
    } finally {
      setIsLoadingSHM(false)
    }
  }, [])

  // Debounced search functions
  const debounce = useCallback((fn: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout | null = null
    return (...args: any[]) => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        fn(...args)
        timer = null
      }, delay)
    }
  }, [])

  const debouncedJobSearch = useMemo(
    () => debounce((value: string) => {
      setJobSearchTerm(value)
      fetchFilterJobs(value)
    }, 300),
    [debounce, fetchFilterJobs]
  )

  const debouncedRecruiterSearch = useMemo(
    () => debounce((value: string) => {
      fetchRecruiters(value)
    }, 300),
    [debounce, fetchRecruiters]
  )

  const debouncedSHMSearch = useMemo(
    () => debounce((value: string) => {
      fetchSHMs(value)
    }, 300),
    [debounce, fetchSHMs]
  )

  const debouncedInterviewsSearch = useMemo(
    () => debounce((value: string) => setInterviewsSearchTerm(value), 500),
    [debounce]
  )

  const fetchInterviews = async (page = 1) => {
    try {
      setInterviewsLoading(true)
      
      const params: any = {
        page,
        limit: interviewsItemsPerPage,
        search: interviewsSearchTerm || undefined,
        jobId: selectedJob !== "all" ? selectedJob : undefined,
        companyName: selectedCompany !== "all" ? selectedCompany : undefined,
        applicationStatus: selectedStatus !== "all" ? selectedStatus : undefined,
        interviewDateFrom: isInterviewTodayStatusFilter(selectedStatus) ? undefined : (selectedDateFrom || undefined),
        interviewDateTo: isInterviewTodayStatusFilter(selectedStatus) ? undefined : (selectedDateTo || undefined),
        _t: Date.now(),
      }

      params.userRole = user?.role
      // For ADMIN: use selected SHM userId if available, otherwise use admin's ID
      // For other roles: use their own ID
      if (user?.role === "ADMIN") {
        params.userId = selectedSHM !== "all" ? selectedSHM : user?.id
      } else {
        params.userId = user?.id
      }


      // Fetch applications for coordinated jobs
      const response = await getCoordinatedinterviews(params)
      
     
      
      if (response.status === "success" && response.data?.applications) {
        // Map applications - the API already returns only coordinated interviews
        const mappedApplications = response.data.applications.map((app: any): Application => {
          return {
            applicationId: app.id || app.applicationId || '',
            appliedOn: app.appliedOn || new Date().toISOString(),
            candidate: {
              id: app.candidate?.id || '',
              name: app.candidate?.name || '',
              email: app.candidate?.email || '',
              phone: app.candidate?.phone || '',
              highestQualification: app.candidate?.highestQualification || '',
              totalExperienceYears: app.candidate?.totalExperienceYears || 0,
              totalExperienceMonths: app.candidate?.totalExperienceMonths || 0,
              skills: app.candidate?.skills || [],
              currentLocationDetails: app.candidate?.currentLocationDetails || {
                city: app.candidate?.currentLocation || '',
                fullAddress: app.candidate?.currentLocation || ''
              },
              profileSource: app.candidate?.profileSource || '',
              createdAt: app.candidate?.createdAt || new Date().toISOString(),
              candidateProfileStrength: app.candidate?.candidateProfileStrength || 0,
            },
            company: {
              companyName: app.company?.companyName || app.job?.companyName || '',
              companyLocation: app.company?.companyLocation || app.job?.location || ''
            },
            job: {
              title: app.job?.title || '',
              id: app.job?.id || ''
            },
            currentStatus: app.currentStatus ? {
              status: app.currentStatus.status || '',
              updatedBy: app.currentStatus.updatedBy || '',
              timestamp: app.currentStatus.timestamp || new Date().toISOString()
            } : {
              status: '',
              updatedBy: '',
              timestamp: new Date().toISOString()
            },
            sourcedBy: app.sourcedBy ? {
              id: app.sourcedBy.id || '',
              username: app.sourcedBy.username || ''
            } : {
              id: '',
              username: ''
            },
            interviewId: app.interviewId,
            roundId: app.roundId,
            interview: {
              id: app.interviewId,
              date: app.interview?.date,
              rounds: app.interview?.rounds || []
            },
            companyCorrected: Boolean(app.companyCorrected),
            companyCorrectedAt: app.companyCorrectedAt || undefined,
            companyCorrectedReason: app.companyCorrectedReason || undefined,
          }
        })
        
        console.log("Mapped applications:", mappedApplications)
        console.log("Setting interviews state with", mappedApplications.length, "applications")
        
        setInterviews(mappedApplications)
        setInterviewsPage(response.page || page)
        setInterviewsTotalPages(response.totalPages || 1)
        setInterviewsTotalResults(response.totalResults || mappedApplications.length)
      } else {
        console.log("No applications in response or invalid response structure")
        setInterviews([])
      }
    } catch (error: any) {
      console.error("Failed to fetch interviews:", error)
      console.error("Error details:", error.response?.data)
      setInterviews([])
      toast.error("Failed to load interviews. Please try again.")
    } finally {
      setInterviewsLoading(false)
    }
  }

  const getStatusBadgeColor = (status?: string) => {
    if (!status) return "bg-gray-100 text-gray-800"
    const statusLower = status.toLowerCase()
    if (statusLower.includes("selected") || statusLower.includes("joined")) {
      return "bg-green-100 text-green-800"
    } else if (statusLower.includes("rejected")) {
      return "bg-red-100 text-red-800"
    } else if (statusLower.includes("pending") || statusLower.includes("scheduled")) {
      return "bg-yellow-100 text-yellow-800"
    } else if (statusLower.includes("interview")) {
      return "bg-blue-100 text-blue-800"
    }
    return "bg-gray-100 text-gray-800"
  }

  const getInterviewDate = (app: Application) => {
    console.log("app::", app)
    if (app.interview?.date) {
      return new Date(app.interview.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    }
    return null
  }

  const handleViewInterviewDetails = async (app: Application) => {
    setSelectedApplication(app)
    setShowInterviewModal(true)
    setShowScheduleForm(false)
    setShowLoaderInInterviewPopup(true)

    try {
      const details = await getInterviewDetailsByApplicationId(app.applicationId)
      setInterviewDetails(details)

      if (details?.data?.interview) {
        const updatedApp = {
          ...app,
          interviewId: details.data.interview.id,
          roundId:
            details.data.interview.rounds?.length > 0
              ? details.data.interview.rounds[details.data.interview.rounds.length - 1].id
              : undefined,
        }
        setSelectedApplication(updatedApp)
      }
    } catch (err: any) {
      setInterviewDetails(null)
    } finally {
      setShowLoaderInInterviewPopup(false)
    }
  }

  const handleResultRound = (round: any, result: "Qualified" | "Rejected") => {
    setCurrentRoundForAction(round)
    setResultFormData({
      result,
      reason: "",
      isFinalRound: false,
    })
    setShowResultModal(true)
  }

  const fetchAndPopulateJobLocation = async (jobId: string) => {
    try {
      const jobDetails = await getJobDetailsById(jobId)
      if (jobDetails && jobDetails.data && jobDetails.data.job) {
        const job = jobDetails.data.job
        let locationText = ""

        if (job.location) {
          locationText = job.location
        } else if (job.addressDetails) {
          const addressParts = []
          if (job.addressDetails.street) addressParts.push(job.addressDetails.street)
          if (job.addressDetails.area) addressParts.push(job.addressDetails.area)
          if (job.addressDetails.city) addressParts.push(job.addressDetails.city)
          if (job.addressDetails.pincode) addressParts.push(job.addressDetails.pincode)
          locationText = addressParts.join(", ")
        }

        if (locationText) {
          setScheduleFormData((prev) => ({
            ...prev,
            location: locationText,
          }))
        }
      }
    } catch (error) {
      console.error("Failed to fetch job location:", error)
    }
  }

  const handleSubmitSchedule = async () => {
    if (!selectedApplication) return

    setIsScheduling(true)
    try {
      const missingFields = []

      if (!scheduleFormData.interviewType) missingFields.push("Interview Type")
      if (!scheduleFormData.scheduledDate) missingFields.push("Interview Date")
      if (!scheduleFormData.timeZone) missingFields.push("Time Zone")
      if (!scheduleFormData.startTime) missingFields.push("Start Time")
      if (!scheduleFormData.endTime) missingFields.push("End Time")
      if (scheduleFormData.interviewType === "Offline" && !scheduleFormData.location?.trim()) {
        missingFields.push("Interview Location")
      }

      if (missingFields.length > 0) {
        toast.error(`Please fill in all required fields:\n\n• ${missingFields.join("\n• ")}`, {
          duration: 6000,
          style: {
            maxWidth: "400px",
            whiteSpace: "pre-line",
            background: "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
            border: "1px solid #ef4444",
            borderLeft: "4px solid #dc2626",
          },
        })
        setIsScheduling(false)
        return
      }

      const validInterviewerIds = scheduleFormData.interviewers.filter(
        (id) => id !== null && id !== undefined && id !== ""
      )

      if (validInterviewerIds.length === 0 && user?.id) {
        validInterviewerIds.push(user.id)
      }

      const scheduledDateForPayload = new Date(scheduleFormData.scheduledDate)
      const scheduledDateISO = scheduledDateForPayload.toISOString().split("T")[0]

      const schedulePayload = {
        interviewType: scheduleFormData.interviewType,
        scheduledDate: scheduledDateISO,
        startTime: scheduleFormData.startTime,
        endTime: scheduleFormData.endTime,
        timeZone: scheduleFormData.timeZone,
        interviewers: validInterviewerIds,
        notes: scheduleFormData.notes || "",
        ...(scheduleFormData.interviewType === "Online" && {
          additionalParticipants: scheduleFormData.additionalParticipants || [],
        }),
        ...(scheduleFormData.interviewType === "Offline" && {
          location: scheduleFormData.location || "",
        }),
      }

      const hasValidInterviewId = selectedApplication.interviewId && selectedApplication.interviewId !== ""
      const hasValidRoundId = currentRoundForAction?.id && currentRoundForAction.id !== ""

      toast.loading("✅ Validation successful! Scheduling interview...", {
        id: "scheduling-interview",
        duration: 2000,
      })

      if (showScheduleModal) {
        await rescheduleInterviewRound(selectedApplication.interviewId || "", currentRoundForAction?.id || "", {
          ...schedulePayload,
          rescheduleReason: "Rescheduled by user",
        })
        toast.success(`Interview has been rescheduled!`, {
          id: "scheduling-interview",
          duration: 6000,
          style: {
            maxWidth: "400px",
            whiteSpace: "pre-line",
          },
        })
        try {
          await new Promise((resolve) => setTimeout(resolve, 1500))
          const updatedDetails = await getInterviewDetailsByApplicationId(selectedApplication.applicationId)
          setInterviewDetails(updatedDetails)
          setShowInterviewModal(false)
          setTimeout(() => setShowInterviewModal(true), 100)
        } catch (fetchErr) {
          console.log("Could not immediately fetch updated details:", fetchErr)
        }
      } else {
        await scheduleInterviewRound(
          selectedApplication.interviewId || "",
          currentRoundForAction?.id || "",
          schedulePayload
        )
        toast.success(`Interview scheduled successfully!`, {
          id: "scheduling-interview",
          duration: 6000,
          style: {
            maxWidth: "400px",
            whiteSpace: "pre-line",
          },
        })
      }

      setShowScheduleModal(false)
      setShowScheduleForm(false)

      try {
        await new Promise((resolve) => setTimeout(resolve, 1500))
        const updatedDetails = await getInterviewDetailsByApplicationId(selectedApplication.applicationId)
        setInterviewDetails(updatedDetails)

        if (!hasValidInterviewId && updatedDetails?.data?.interview?.id) {
          setSelectedApplication((prev) =>
            prev
              ? {
                  ...prev,
                  interviewId: updatedDetails.data.interview.id,
                  roundId:
                    updatedDetails.data.interview.rounds?.length > 0
                      ? updatedDetails.data.interview.rounds[updatedDetails.data.interview.rounds.length - 1].id
                      : undefined,
                }
              : null
          )
        }
      } catch (fetchErr) {
        console.log("Could not immediately fetch updated details:", fetchErr)
      }
    } catch (error: any) {
      console.error("Failed to schedule interview:", error)
      toast.error(error.response?.data?.message || "Failed to schedule interview. Please try again.", {
        id: "scheduling-interview",
      })
    } finally {
      setIsScheduling(false)
    }
  }

  const handleSubmitResult = async () => {
    if (!currentRoundForAction || !selectedApplication) return

    if (!resultFormData.reason.trim()) {
      toast.error("Please provide a detailed reason for your decision", {
        duration: 4000,
      })
      return
    }

    if (resultFormData.reason.trim().length < 10) {
      toast.error("Please provide a more detailed reason (at least 10 characters)", {
        duration: 4000,
      })
      return
    }

    if (resultFormData.isFinalRound) {
      showConfirmation(
        "Final Round Confirmation",
        `This is the final round. Are you sure you want to mark this candidate as ${resultFormData.result}?\n\nThis action will complete the interview process.`,
        () => {
          submitInterviewResult()
        },
        {
          confirmText: `Mark as ${resultFormData.result}`,
          cancelText: "Cancel",
          type: resultFormData.result === "Rejected" ? "danger" : "warning",
          onCancel: () => {
            toast("Action cancelled. No changes made.", {
              duration: 3000,
            })
          },
        }
      )
      return
    }

    submitInterviewResult()
  }

  const submitInterviewResult = async () => {
    if (!currentRoundForAction || !selectedApplication) return

    setIsSubmittingResult(true)
    try {
      const payload = {
        outcome: resultFormData.result,
        feedback: {
          rating: 5,
          recommendation: resultFormData.result,
          comments: resultFormData.reason,
          ...(resultFormData.isFinalRound &&
            resultFormData.expectedJoiningDate && { expectedJoiningDate: resultFormData.expectedJoiningDate }),
        },
        isFinalRound: resultFormData.isFinalRound,
      }

      await updateInterviewRoundStatus(selectedApplication.interviewId!, currentRoundForAction.id, payload)

      const candidateName = selectedApplication.candidate.name
      const roundName = currentRoundForAction.roundName || "Round"

      if (resultFormData.result === "Qualified") {
        if (resultFormData.isFinalRound) {
          toast.success(
            `Congratulations!\n${candidateName} has been selected!\n\n${roundName} completed\nFinal decision: Qualified\nInterview process completed successfully`,
            {
              duration: 8000,
              style: {
                maxWidth: "450px",
                whiteSpace: "pre-line",
              },
            }
          )
        } else {
          toast.success(
            `Interview result recorded\n\n${candidateName}\n${roundName} completed\nDecision: ${resultFormData.result}\nFeedback has been saved`,
            {
              duration: 6000,
              style: {
                maxWidth: "400px",
                whiteSpace: "pre-line",
              },
            }
          )
        }
      } else {
        toast.success(
          `Interview result recorded\n\n${candidateName}\n${roundName} completed\nDecision: ${resultFormData.result}\nFeedback has been saved`,
          {
            duration: 6000,
            style: {
              maxWidth: "400px",
              whiteSpace: "pre-line",
            },
          }
        )
      }

      setShowResultModal(false)
      setResultFormData({
        result: "Qualified",
        reason: "",
        isFinalRound: false,
      })

      // Refresh interview details
      if (selectedApplication) {
        await handleViewInterviewDetails(selectedApplication)
      }
    } catch (error: any) {
      console.error("Failed to submit interview result:", error)
      toast.error(error.response?.data?.message || "Failed to submit interview result. Please try again.")
    } finally {
      setIsSubmittingResult(false)
    }
  }

  const handleChangeCandidateStatus = async (app: Application) => {
    const currentStatus = app.currentStatus?.status || ""
    setStatusUpdateData({
      applicationId: app.applicationId,
      newStatus: getDefaultStatusChangeValue(currentStatus),
      comments: "",
      currentStatus,
    })
    setShowStatusUpdateModal(true)
  }

  const handleInterviewStatusChange = (app: Application, newStatus: string) => {
    const candidateName = app.candidate.name || "this candidate"
    showConfirmation(
      `Confirm ${newStatus === "Interview Attended" ? "Attendance" : "Non-Attendance"}`,
      `Are you sure you want to mark ${candidateName}'s interview as "${newStatus}"?`,
      async () => {
        try {
          await updateApplicationStatus(app.applicationId, {
            newStatus,
            comments: `Interview status updated to ${newStatus} by ${user?.username || "user"}`,
          })
          toast.success(`Interview status updated to ${newStatus}`)
          await fetchInterviews(interviewsPage)
        } catch (err: any) {
          toast.error(err.message || "Failed to update interview status", {
            duration: 4000,
          })
        }
      },
    )
  }

  const handleUpdateStatus = async (
    applicationId: string,
    newStatus: string,
    comments?: string,
    selectedJobId?: string,
    joiningDate?: string
  ) => {
    try {
      if (newStatus === "Reassign Company" && selectedJobId) {
        setIsReassigning(true)
        await reassignCandidateApplication(applicationId, {
          newJobId: selectedJobId,
          comments: comments || `Reassigned by ${user?.username || "user"}`,
        })
        toast.success("Application reassigned successfully!")
      } else {
        await updateApplicationStatus(applicationId, {
          newStatus,
          comments: comments || `Status updated to ${newStatus} by ${user?.username || "user"}`,
          ...(newStatus === "Offered" && { offeredDate: new Date().toISOString().split("T")[0] }),
          ...(newStatus === "Joined" && { joiningDate: joiningDate }),
        })
        toast.success(`Application status updated to ${newStatus}`)
      }
      await fetchInterviews(interviewsPage)
    } catch (err: any) {
      toast.error(err.message || "Failed to update status", {
        duration: 4000,
      })
    } finally {
      setIsReassigning(false)
    }
  }

  const handleUpdateJoiningDate = async () => {
    try {
      if (!statusUpdateData.applicationId) return
      if (!joiningDate) {
        toast.error("Please select a valid joining date.", { duration: 3000 })
        return
      }
      await updateJoiningDate({applicationId: statusUpdateData.applicationId, comments: statusUpdateData.comments, joiningDate})
      toast.success(`Application joining date updated to ${joiningDate}`)
      await fetchInterviews(interviewsPage)
    } catch (error: any) {
      toast.error(error.message || "Failed to update joining date", {
        duration: 4000,
      })
    } finally {
      setShowJoiningDateUpdateModal(false)
    }
  }

  const handleCoordinatedInterviewsExcelDownload = async () => {
    if(user?.role === "ADMIN" && selectedSHM === "all"){
      toast.error("Please select an SHM to export coordinated interviews.")
      return
    }
    
    const params: any = {
      search: interviewsSearchTerm || undefined,
      jobId: selectedJob !== "all" ? selectedJob : undefined,
      companyName: selectedCompany !== "all" ? selectedCompany : undefined,
      applicationStatus: selectedStatus !== "all" ? selectedStatus : undefined,
      interviewDateFrom: isInterviewTodayStatusFilter(selectedStatus) ? undefined : (selectedDateFrom || undefined),
      interviewDateTo: isInterviewTodayStatusFilter(selectedStatus) ? undefined : (selectedDateTo || undefined),
      page: interviewsPage,
      limit: interviewsItemsPerPage,
    }

    params.userRole = user?.role
    // For ADMIN: use selected SHM userId if available, otherwise use admin's ID
    // For other roles: use their own ID
    if (user?.role === "ADMIN") {
      params.userId = selectedSHM !== "all" ? selectedSHM : user?.id
    } else {
      params.userId = user?.id
    }

    await exportCoordinatedInterviewsExcel(params)
  }

  const renderActionsDropdown = (app: Application) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation()
            handleChangeCandidateStatus(app)
          }}
          disabled={!CHANGE_STATUS_ENABLED_STATUSES.includes(app.currentStatus?.status || "")}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Change Status
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation()
            setStatusUpdateData({
              applicationId: app.applicationId,
              newStatus: "Reassign Company",
              comments: "",
              selectedJobId: "",
            })
            setShowStatusUpdateModal(true)
          }}
          disabled={app.currentStatus?.status === "Joined"}
        >
          <UserCheck className="mr-2 h-4 w-4" />
          Reassign Company
        </DropdownMenuItem>
        {user?.role === "ADMIN" && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              setTimeout(async () => {
                setChangeCompanyData({
                  applicationId: app.applicationId,
                  selectedJobId: "",
                  selectedJobLabel: "",
                  comments: "",
                  candidateName: app.candidate?.name || "",
                  currentCompany: app.company?.companyName || "",
                })
                setShowChangeCompanyModal(true)
                setIsLoadingChangeCompanyJobs(true)
                try {
                  const response = await getJobs({ status: "published", limit: 50, page: 1 })
                  setChangeCompanyJobs(response?.data?.jobs || [])
                } catch {
                  setChangeCompanyJobs([])
                } finally {
                  setIsLoadingChangeCompanyJobs(false)
                }
              }, 0)
            }}
            disabled={CHANGE_COMPANY_BLOCKED_STATUSES.includes(app.currentStatus?.status || "")}
          >
            <Building className="mr-2 h-4 w-4" />
            Change Company
          </DropdownMenuItem>
        )}
        {(["Joined","Tenure Pending","Tenure Approved"].includes(app.currentStatus?.status || "") && user?.role === "ADMIN") && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              setStatusUpdateData((prev) => ({ ...prev, applicationId: app.applicationId }))
              setShowJoiningDateUpdateModal(true)
            }}
          >
            <UserCheck className="mr-2 h-4 w-4" />
            Change Joining Date
          </DropdownMenuItem>
        )}
        {(app.currentStatus?.status === "Interview Scheduled" || app.currentStatus?.status === "Interview Rescheduled") && (
          <>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                handleInterviewStatusChange(app, "Interview Attended")
              }}
            >
              <UserCheck className="mr-2 h-4 w-4" />
              Interview Attended
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                handleInterviewStatusChange(app, "Interview Not Attended")
              }}
            >
              <UserCheck className="mr-2 h-4 w-4" />
              Interview Not Attended
            </DropdownMenuItem>
          </>
        )}
        {(user?.role === "ADMIN" || user?.role === "SHM") && app.candidate?.id && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditCandidateApp(app)
                    setEditCandidateForm({
                      name: app.candidate.name || "",
                      email: app.candidate.email || "",
                      phone: app.candidate.phone || "",
                    })
                    setEditCandidateFieldErrors({ name: "", email: "", phone: "" })
                    setShowEditCandidateModal(true)
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit candidate
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px] text-xs">
                Admin / SHM only. Updates name, email, and phone on the candidate profile. Each save is logged with
                previous and new values.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  useEffect(() => {
    if (statusUpdateData.newStatus === "Joined") {
      setShowDateSelect(true)
    } else {
      setJoiningDate(new Date().toISOString().split("T")[0])
      setShowDateSelect(false)
    }
  }, [statusUpdateData.newStatus])

  useEffect(() => {
    // Only fetch jobs when jobs tab is active
    if (activeTab === "jobs") {
      if (user?.role === "AGENCY" || user?.role === "SHM" || user?.role === "HM") {
        fetchJobs(currentPage, selectedSHM)
      } else if (user?.role === "ADMIN") {
        if (selectedSHM !== "all") {
          // Only fetch jobs for ADMIN if an SHM is selected
          fetchJobs(currentPage, selectedSHM)
        } else {
          // Clear jobs when no SHM is selected for ADMIN
          setJobs([])
          setTotalResults(0)
          setTotalPages(1)
          setCurrentPage(1)
          setLoading(false)
          setIsLoading(false)
        }
      }
    }
  }, [activeTab, currentPage, itemsPerPage, searchTerm, statusFilter, user?.role, selectedSHM])

  useEffect(() => {
    setCurrentPage(1)
    localStorage.setItem('coordinationsItemsPerPage', String(itemsPerPage))
  }, [itemsPerPage])

  // Restore state when navigating back from viewdetails (must run before reset logic)
  useEffect(() => {
    const state = location.state as { pageNumber?: number; selectedSHM?: string } | null
    if (state && !hasRestoredStateRef.current) {
      // Mark restoration as in progress immediately
      hasRestoredStateRef.current = true
      isInitialMountRef.current = false
      
      // Restore page number for both ADMIN and SHM users
      if (state.pageNumber) {
        restoredPageRef.current = state.pageNumber
        setCurrentPage(state.pageNumber)
      }
      // Restore selectedSHM only for ADMIN users
      if (state.selectedSHM && user?.role === "ADMIN") {
        setSelectedSHM(state.selectedSHM)
      }
      
      // Clear location.state after restoration is complete
      // Use a small delay to ensure state updates are applied first
      setTimeout(() => {
        navigate(location.pathname + location.search, { replace: true, state: {} })
        // Clear restored page ref after navigation completes
        setTimeout(() => {
          restoredPageRef.current = null
          hasRestoredStateRef.current = false
        }, 200)
      }, 50)
    } else if (!state) {
      // Reset the ref when state is cleared (e.g., on new navigation)
      hasRestoredStateRef.current = false
      restoredPageRef.current = null
      // Mark that initial mount is complete after first render
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false
      }
    }
  }, [location.state, user?.role, navigate, location.pathname, location.search])

  // Reset page when filters change (but not when restoring state from navigation)
  // This should only run when user actively changes filters, not during restoration
  useEffect(() => {
    // Skip on initial mount if we have restored state
    if (isInitialMountRef.current && restoredPageRef.current !== null) {
      return
    }
    
    // Only reset if we're not currently restoring state from navigation
    // The refs ensure we don't reset during or immediately after restoration
    if (!hasRestoredStateRef.current && 
        restoredPageRef.current === null && 
        (user?.role === "SHM" || user?.role === "ADMIN")) {
      setCurrentPage(1)
    }
  }, [searchTerm, statusFilter, user?.role])
  
  // Separate effect for selectedSHM to handle ADMIN user SHM selection
  // This should not reset page when restoring selectedSHM
  useEffect(() => {
    // Skip on initial mount if we have restored state
    if (isInitialMountRef.current && restoredPageRef.current !== null) {
      return
    }
    
    // Only reset if we're not restoring and selectedSHM actually changed (not initial restore)
    if (!hasRestoredStateRef.current && 
        restoredPageRef.current === null && 
        user?.role === "ADMIN") {
      setCurrentPage(1)
    }
  }, [selectedSHM, user?.role])

  // Fetch filter data on mount
  useEffect(() => {
    // Fetch SHMs for ADMIN users
    if (user?.role === "ADMIN") {
      fetchSHMs()
    }
  }, [user?.role, fetchSHMs])

  useEffect(() => {
    if (activeTab === "interviews") {
      fetchFilterJobs()
    }
  }, [activeTab, fetchFilterJobs, selectedSHM])

  useEffect(() => {
    setSelectedJob("all")
    setSelectedCompany("all")
  }, [selectedSHM])

  // Reset page when filters change
  useEffect(() => {
    if ((user?.role === "SHM" || user?.role === "ADMIN" || user?.role === "HM" || user?.role === "AGENCY") && activeTab === "interviews") {
      setInterviewsPage(1)
    }
  }, [selectedStatus, selectedJob, selectedCompany, selectedSHM, selectedDateFrom, selectedDateTo, interviewsSearchTerm, user?.role, activeTab])


  // Fetch interviews when filters or page changes (only when interviews tab is active)
  useEffect(() => {
    // Only fetch interviews when interviews tab is active
    if (activeTab !== "interviews") {
      return
    }

    if (user?.role === "SHM" || user?.role === "HM" || user?.role === "AGENCY") {
      fetchInterviews(interviewsPage)
    } else if (user?.role === "ADMIN") {
      if (selectedSHM !== "all") {
        // Only fetch interviews for ADMIN if an SHM is selected
        fetchInterviews(interviewsPage)
      } else if (selectedSHM === "all") {
        // Clear interviews when no SHM is selected for ADMIN
        setInterviews([])
        setInterviewsTotalResults(0)
        setInterviewsTotalPages(1)
        setInterviewsPage(1)
        setInterviewsLoading(false)
      }
    }
  }, [activeTab, interviewsPage, interviewsItemsPerPage, user?.role, selectedStatus, selectedJob, selectedCompany, selectedSHM, selectedDateFrom, selectedDateTo, interviewsSearchTerm])

  useEffect(() => {
    console.log("Interviews state updated:", interviews)
    console.log("Interviews length:", interviews.length)
    console.log("Interviews loading:", interviewsLoading)
  }, [interviews, interviewsLoading])

  // Only SHM users should access this page
  if (user?.role !== "SHM" && user?.role !== "ADMIN" && user?.role !== "HM" && user?.role !== "AGENCY") {
    console.log("User role:", user?.role)
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You do not have permission to access this page.</p>
        </div>
      </div>
    )
  }

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && !isLoading) {
      setCurrentPage(newPage)
    }
  }

  const renderPagination = () => {
    const pageNumbers = []
    const maxPagesToShow = 6

    let startPage = Math.max(1, currentPage - 2)
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i)
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {pageNumbers.map((page) => (
          <Button
            key={page}
            variant={currentPage === page ? "default" : "outline"}
            size="sm"
            onClick={() => handlePageChange(page)}
            disabled={isLoading}
            className={currentPage === page ? "bg-[#EC6E51] text-primary-foreground" : ""}
          >
            {page}
          </Button>
        ))}

        {endPage < totalPages && (
          <>
            <span className="text-sm text-muted-foreground">...</span>
            <Button variant="outline" size="sm" onClick={() => handlePageChange(totalPages)} disabled={isLoading}>
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    )
  }

  const handleViewJobDetails = async (jobId: string, currentPage: number) => {
    navigate(`/viewdetails/${jobId}`, { state: { currentPage,source: "coordinations",selectedSHM } })
  }

  const handleEditJob = (job: Job) => {
    navigate(`/create-job?mode=edit&jobId=${job.id}&t=${Date.now()}`)
  }

//   const getStatusBadgeColor = (status: string) => {
//     switch (status.toLowerCase()) {
//       case "published":
//         return "bg-green-100 text-green-800"
//       case "drafted":
//         return "bg-yellow-100 text-yellow-800"
//       case "archived":
//         return "bg-gray-100 text-gray-800"
//       default:
//         return "bg-blue-100 text-blue-800"
//     }
//   }

  const getWorkTypeColor = (workType: string) => {
    switch (workType?.toLowerCase()) {
      case "remote":
        return "bg-orange-50 text-orange-700 border-orange-200"
      case "hybrid":
        return "bg-blue-50 text-blue-700 border-blue-200"
      case "on-site":
        return "bg-orange-50 text-orange-700 border-orange-200"
      default:
        return "bg-gray-50 text-gray-700 border-gray-200"
    }
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    })
  }



  return (
    <div data-page-scroll="internal" className="flex-1 min-h-0 flex flex-col overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden relative">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-4 flex-shrink-0">
            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-2 sm:space-x-4 flex-1">
                
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                    {activeTab === "jobs" ? "Assigned Jobs" : "Interviews"}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {activeTab === "jobs" ? (
                      <span>Total Jobs: {totalResults}</span>
                    ) : (
                      <span>Manage and track interview applications</span>
                    )}
                  </p>
                </div>
              </div>
              {user?.role === "ADMIN" && (
                  <div className="min-w-[200px] sm:min-w-[250px] mr-4">
                    <SearchableSelect
                      options={[
                        { value: "all", label: "Select SHMs/HMs" },
                        ...shmUsers.map((shm: UserType) => ({
                          value: shm.id,
                          label: (
                            <div className="text-left">
                              <div>{shm.username} <span className="text-sm text-gray-500">{shm.role}</span></div>
                            </div>
                          ),
                          searchText: `${shm.username} ${shm.email}`.toLowerCase(),
                        })),
                      ]}
                      value={selectedSHM}
                      onValueChange={(value) => {
                        console.log("value",value);
                        setSelectedSHM(value)
                        setCurrentPage(1)
                        setInterviewsPage(1)
                      }}
                      onSearchChange={debouncedSHMSearch}
                      placeholder={isLoadingSHM ? "Loading SHMs/HMs..." : "Select SHM/HM"}
                      searchPlaceholder="Search SHMs/HMs..."
                      icon={<User className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />}
                      emptyText="No SHMs available"
                      className="w-full rounded-lg border-gray-200 text-black focus:ring-2 focus:ring-orange-500 h-9 sm:h-10"
                      loading={isLoadingSHM}
                    />
                  </div>
                )}

              <div className="flex items-center gap-1">
              <TabsList className="grid w-auto grid-cols-2">
                <TabsTrigger value="jobs" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Jobs
                </TabsTrigger>
                <TabsTrigger value="interviews" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Interviews
                </TabsTrigger>
              </TabsList>
              {activeTab === "interviews" && (
                <button
                  type="button"
                  aria-label={showInterviewFilters ? "Hide filters" : "Show filters"}
                  onClick={() => setShowInterviewFilters((open) => !open)}
                  className="h-8 w-8 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                >
                  {showInterviewFilters ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>
              )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="sm:hidden"
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <TabsContent
            value="jobs"
            className="mt-0 flex-1 min-h-0 flex-col overflow-hidden data-[state=inactive]:hidden data-[state=active]:flex"
          >
            <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-4 flex-shrink-0">
              <div
                className={`flex flex-col space-y-4 mt-0 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:space-x-4 ${
                  showMobileFilters ? "block" : "hidden sm:flex"
                }`}
              >
                <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
                  <div className="relative flex-1 sm:flex-none">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full sm:w-64 border-gray-300"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 relative" style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <div className="flex-1 overflow-y-auto p-3 sm:p-6" style={{ paddingBottom: '90px' }}>
          {loading ? (
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {[...Array(12)].map((_, index) => (
                <Card
                  key={`shimmer-${index}`}
                  className="group bg-white border-0 shadow-sm rounded-2xl overflow-hidden"
                >
                  <CardContent className="flex flex-col justify-between h-full p-0">
                    <div className="flex flex-col gap-4 p-6 pb-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="animate-pulse bg-gray-200 rounded-full w-20 h-6"></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="animate-pulse bg-gray-200 rounded w-full h-6 mb-2"></div>
                        <div className="flex items-center gap-2">
                          <div className="animate-pulse bg-gray-200 rounded-full w-7 h-7"></div>
                          <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="animate-pulse bg-gray-200 rounded w-full h-8"></div>
                        <div className="flex gap-2">
                          <div className="animate-pulse bg-gray-200 rounded w-24 h-6"></div>
                          <div className="animate-pulse bg-gray-200 rounded w-20 h-6"></div>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center gap-4">
                          <div className="flex items-center gap-2">
                            <div className="animate-pulse bg-gray-200 rounded w-16 h-4"></div>
                            <div className="animate-pulse bg-gray-200 rounded w-8 h-4"></div>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="animate-pulse bg-gray-200 rounded w-4 h-4"></div>
                            <div className="animate-pulse bg-gray-200 rounded w-16 h-4"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 py-4">
                      <div className="animate-pulse bg-gray-200 rounded-lg w-full h-10"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : jobs?.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center px-4">
                <p className="text-gray-500 mb-2">
                  {user?.role === "ADMIN" && selectedSHM === "all" 
                    ? "Please select an SHM to view jobs" 
                    : "No jobs found"}
                </p>
                <p className="text-sm text-gray-400">
                  {user?.role === "ADMIN" && selectedSHM === "all"
                    ? "Select an SHM from the dropdown above to see their assigned jobs"
                    : searchTerm 
                      ? "Try adjusting your search terms" 
                      : "No assigned jobs available"}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {jobs?.map((job) => (
                <Card
                  key={job.id}
                  className="group bg-white border-0 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 rounded-2xl overflow-hidden"
                >
                  <CardContent className="flex flex-col justify-between h-full p-0">
                    <div className="flex flex-col gap-4 p-6 pb-0">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* <Badge
                            variant="outline"
                            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${getStatusBadgeColor(job.status)}`}
                          >
                            {job.status === "published" ? "Published" : job.status === "drafted" ? "Draft" : "Archived"}
                          </Badge> */}
                          {job.isExternal && (
                            <Badge
                              variant="outline"
                              className="text-xs font-semibold px-3 py-1.5 rounded-full border bg-orange-100 text-orange-800 border-orange-200"
                            >
                              External
                            </Badge>
                          )}
                          {job.eligibleForCampusDrive && (
                            <Badge
                              variant="outline"
                              className="text-xs font-semibold px-3 py-1.5 rounded-full border bg-blue-100 text-blue-800 border-blue-200"
                            >
                              CD
                            </Badge>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0 hover:bg-gray-50 rounded-full transition-colors duration-200"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4 text-gray-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditJob(job)
                              }}
                              className="cursor-pointer"
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit Job
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-lg font-bold text-gray-900 line-clamp-2 leading-tight group-hover:text-[#EC6E51] transition-colors duration-200">
                          {job.title}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          {job.isExternal ? (
                            <>
                              <Building2 className="h-4 w-4 text-orange-500" />
                              <span className="font-medium text-orange-600">External</span>
                            </>
                          ) : (
                            <>
                              {job.companyLogoUrl ? (
                                <img 
                                  src={job.companyLogoUrl} 
                                  alt={`${job.companyName} logo`}
                                  className="h-7 w-7 rounded-full object-cover border border-gray-200"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.style.display = 'block';
                                  }}
                                />
                              ) : (
                                <Building2 className="h-4 w-4 text-gray-400" />
                              )}
                              <Building2 className="h-4 w-4 text-gray-400" style={{display: 'none'}} />
                              <span className="font-medium">{job.companyName}</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Badge
                          variant="outline"
                          className="text-xs font-medium px-2 py-1 rounded-md bg-gray-50 text-gray-700 border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                          style={{maxWidth:"100%"}}
                          title={`${job.location} (Click to copy)`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(job.location);
                            toast.success("Location copied to clipboard!");
                          }}
                        >
                          <div> <MapPin className="h-[13px] w-[13px] mr-1" /></div>
                          <div className="truncate">
                            {job.location}
                          </div>
                        </Badge>
                        <div className="flex gap-2">
                          {job.employmentType && (
                            <Badge
                              variant="outline"
                              className="text-sm font-medium px-3 py-1.5 rounded-md bg-gray-50 text-gray-700 border-gray-200 flex items-center gap-1"
                              style={{width:"62%"}}
                            >
                              <Clock className="h-4 w-4" />
                              <div className="truncate">
                                {job.employmentType}
                              </div>
                            </Badge>
                          )}
                          <Badge
                            variant="outline"
                            className={`text-xs font-medium px-2 py-1 rounded-md ${getWorkTypeColor(job.workType)}`}
                          >
                            <Briefcase className="h-3 w-3 mr-1" />
                            {job.workType}
                          </Badge>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center gap-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Openings:</span>
                            <span className="text-sm font-bold text-gray-900">{job.noOfOpenings}</span>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Calendar className="h-4 w-4 text-[#EC6E51]" />
                            <span>{formatDate(job.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 py-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-[#EC6E51] hover:text-white hover:bg-[#EC6E51] font-medium px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 border border-[#EC6E51] hover:border-transparent"
                        onClick={() => handleViewJobDetails(job.id, currentPage)}
                      >
                        View Details
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
              </div>
              <div className="bg-white border-t border-gray-200 shadow-lg absolute bottom-0 left-0 right-0 z-10">
                <div className="flex items-center justify-between p-4">
                <div className="text-sm text-gray-500">
                  {totalResults > 0 && jobs.length > 0 ? (
                    <>
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalResults)} of {totalResults} jobs
                    </>
                  ) : totalResults > 0 ? (
                    `Page ${currentPage} of ${totalPages} (${totalResults} total jobs)`
                  ) : (
                    "No jobs found"
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {totalPages > 1 && renderPagination()}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Per page:</span>
                    <Select value={String(itemsPerPage)} onValueChange={(value) => setItemsPerPage(Number(value))}>
                      <SelectTrigger className="w-20 h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="12">12</SelectItem>
                        <SelectItem value="24">24</SelectItem>
                        <SelectItem value="36">36</SelectItem>
                        <SelectItem value="48">48</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </TabsContent>

          {activeTab === "interviews" && (
            <TabsContent
              value="interviews"
              className="mt-0 flex-1 min-h-0 flex-col overflow-hidden data-[state=inactive]:hidden data-[state=active]:flex"
            >
              {/* Filters Section */}
              {showInterviewFilters && (
              <div className="bg-white border-b border-gray-200 px-2 sm:px-6 py-3 sm:py-4 flex-shrink-0">
                <div className="w-full">
                  {/* Search Bar */}
                  <div className="mb-3 sm:mb-4">
                    <div className="flex flex-col justify-between sm:flex-row items-stretch sm:items-center gap-2">
                      <div className="relative flex-1 sm:flex-none">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <Input
                          placeholder="Search Applications..."
                          value={interviewsSearchTerm}
                          onChange={(e) => {
                            setInterviewsSearchTerm(e.target.value)
                            debouncedInterviewsSearch(e.target.value)
                          }}
                          className="pl-10 w-full sm:w-64 border-gray-300 h-9 sm:h-10"
                        />
                      </div>
                      <Button
                        variant="outline"
                        onClick={handleCoordinatedInterviewsExcelDownload}
                        className="flex items-center justify-center gap-2 rounded-lg border-orange-300 text-orange-600 hover:bg-orange-50 w-full sm:w-auto"
                      >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Download Excel</span>
                        {/* <span className="sm:hidden">Excel</span> */}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Filter Grid */}
                  <div className="flex flex-wrap items-center gap-2">
                    <SearchableSelect
                      options={jobFilterOptions}
                      value={selectedJob}
                      onValueChange={(value) => {
                        setSelectedJob(value)
                        setInterviewsPage(1)
                      }}
                      onSearchChange={debouncedJobSearch}
                      placeholder={
                        user?.role === "ADMIN" && selectedSHM === "all"
                          ? "Select SHM/HM first"
                          : isLoadingJobs
                            ? "Loading jobs..."
                            : "All Jobs"
                      }
                      searchPlaceholder="Search jobs..."
                      icon={<Briefcase className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />}
                      emptyText="No assigned jobs available"
                      className="w-fit shrink-0 rounded-lg border-gray-200 text-black focus:ring-2 focus:ring-orange-500 h-9"
                      loading={isLoadingJobs}
                      disabled={user?.role === "ADMIN" && selectedSHM === "all"}
                    />
                    {!isInterviewTodayStatusFilter(selectedStatus) && (
                      <Input
                        type="date"
                        value={selectedDateFrom}
                        onChange={(e) => {
                          setSelectedDateFrom(e.target.value)
                          setInterviewsPage(1)
                        }}
                        max={selectedDateTo || undefined}
                        className="w-fit shrink-0 h-9 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                        title="From Date"
                        placeholder="From Date"
                      />
                    )}
                    {!isInterviewTodayStatusFilter(selectedStatus) && (
                      <Input
                        type="date"
                        value={selectedDateTo}
                        onChange={(e) => {
                          setSelectedDateTo(e.target.value)
                          setInterviewsPage(1)
                        }}
                        min={selectedDateFrom || undefined}
                        className="w-fit shrink-0 h-9 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                        title="To Date"
                        placeholder="To Date"
                      />
                    )}
                    <SearchableSelect
                      options={companyOptions}
                      value={selectedCompany}
                      onValueChange={(value) => {
                        setSelectedCompany(value)
                        if (value !== "all" && selectedJob !== "all") {
                          const job = filterJobs.find((j) => j.id === selectedJob)
                          if (job && getJobCompanyName(job) !== value) {
                            setSelectedJob("all")
                          }
                        }
                        setInterviewsPage(1)
                      }}
                      onSearchChange={debouncedJobSearch}
                      placeholder={
                        user?.role === "ADMIN" && selectedSHM === "all"
                          ? "Select SHM/HM first"
                          : isLoadingJobs
                            ? "Loading companies..."
                            : "All Companies"
                      }
                      searchPlaceholder="Search companies..."
                      icon={<Building className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />}
                      emptyText="No assigned companies available"
                      className="w-fit shrink-0 rounded-lg border-gray-200 text-black focus:ring-2 focus:ring-orange-500 h-9"
                      loading={isLoadingJobs}
                      disabled={user?.role === "ADMIN" && selectedSHM === "all"}
                    />
                    <SearchableSelect
                      options={APPLICATION_STATUS_FILTER_OPTIONS}
                      value={selectedStatus}
                      onValueChange={(v) => {
                        setSelectedStatus(v)
                        if (isInterviewTodayStatusFilter(v)) {
                          setSelectedDateFrom("")
                          setSelectedDateTo("")
                        }
                        setInterviewsPage(1)
                      }}
                      placeholder="All Status"
                      searchPlaceholder="Search status..."
                      icon={<CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />}
                      emptyText="No status available"
                      className="w-fit shrink-0 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 h-9"
                    />
                  </div>
                </div>
              </div>
              )}
              
              <div className="flex-1 min-h-0 overflow-auto w-full">
                <div className="relative w-full">
                  <div className="hidden lg:block w-full">
                    <table className="min-w-full divide-y divide-border" style={{ width: '100%', margin: 0, padding: 0, tableLayout: 'auto' }}>
                      <thead style={{ backgroundColor: "#FFF5F2" }}>
                        <tr>
                          <th className="px-4 py-3 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-48 sticky top-0 left-0 bg-[#FFF5F2] z-20 border-r border-border">
                            Candidate Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-48 sticky top-0 bg-[#FFF5F2] z-10 border-r border-border">
                            Company Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-48 sticky top-0 bg-[#FFF5F2] z-10 border-r border-border">
                            Company Location
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-64 sticky top-0 bg-[#FFF5F2] z-10">
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                            Phone
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-48 sticky top-0 bg-[#FFF5F2] z-10">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-48 sticky top-0 bg-[#FFF5F2] z-10">
                            Sourced By
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-40 sticky top-0 bg-[#FFF5F2] z-10">
                            Applied On
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                            Interview Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                            Interview
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-background divide-y divide-border">
                        {interviewsLoading ? (
                          <>
                            {[...Array(10)].map((_, index) => (
                              <tr key={`shimmer-${index}`} className="bg-background divide-y divide-border">
                                <td className="px-4 py-4"></td>
                                <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border">
                                  <div className="flex items-center gap-3">
                                    <div className="animate-pulse bg-gray-200 rounded-full w-8 h-8"></div>
                                    <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                                  </div>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="animate-pulse bg-gray-200 rounded w-40 h-4"></div>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="animate-pulse bg-gray-200 rounded w-48 h-4"></div>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="animate-pulse bg-gray-200 rounded w-56 h-4"></div>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="animate-pulse bg-gray-200 rounded-full w-20 h-6"></div>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="animate-pulse bg-gray-200 rounded w-28 h-4"></div>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="animate-pulse bg-gray-200 rounded w-16 h-4"></div>
                                </td>
                                <td className="px-4 py-4">
                                  <div className="animate-pulse bg-gray-200 rounded w-8 h-8"></div>
                                </td>
                              </tr>
                            ))}
                          </>
                        ) : interviews.length === 0 ? (
                          <tr style={{ height: '100%' }}>
                            <td colSpan={12} className="px-4 py-12 text-center overflow-hidden">
                              <p className="text-muted-foreground">
                                {user?.role === "ADMIN" && selectedSHM === "all"
                                  ? "Please select an SHM to view interviews"
                                  : "No interviews found for your coordinated jobs."}
                              </p>
                              {user?.role === "ADMIN" && selectedSHM === "all" && (
                                <p className="text-sm text-muted-foreground mt-2">
                                  Select an SHM from the dropdown above to see their interviews
                                </p>
                              )}
                            </td>
                          </tr>
                        ) : (
                          (() => {
                            console.log("Rendering interviews, count:", interviews.length, "data:", interviews)
                            return interviews.map((app) => (
                            <tr
                              key={app.applicationId}
                              className={cn("hover:bg-muted/30 transition-colors")}
                            >
                              <td className="px-4 py-4"></td>
                              <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border relative group">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium transition-colors cursor-pointer text-foreground hover:text-primary">
                                    {app.candidate.name}
                                  </span>
                                </div>
                                <div className="absolute top-0 left-[calc(100%+0.5rem)] mt-1 w-80 p-4 bg-white shadow-lg rounded border border-border opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto z-30">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-lg">{app.candidate.name}</h4>
                                    <Badge variant="secondary" className="text-xs">
                                      {app.currentStatus?.status || "N/A"}
                                    </Badge>
                                  </div>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Job:</span>
                                      <span className="font-medium">{app.job?.title || "N/A"}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Location:</span>
                                      <div className="max-w-48 truncate" title={app.candidate.currentLocationDetails?.fullAddress}>
                                        {app.company.companyLocation || "N/A"}
                                      </div>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Phone:</span>
                                      <span className="font-medium">{app.candidate.phone}</span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-foreground">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span>{app.company?.companyName}</span>
                                  {app.companyCorrected && (
                                    <TooltipProvider delayDuration={200}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Badge
                                            variant="outline"
                                            className="text-[10px] px-1.5 py-0 h-5 border-amber-300 bg-amber-50 text-amber-800 font-medium"
                                          >
                                            Changed
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                          <p className="font-medium">Company corrected by Admin</p>
                                          {app.companyCorrectedAt && (
                                            <p className="text-xs opacity-90 mt-0.5">
                                              {format(new Date(app.companyCorrectedAt), "dd MMM yyyy")}
                                            </p>
                                          )}
                                          {app.companyCorrectedReason && (
                                            <p className="text-xs mt-1.5 whitespace-pre-wrap">
                                              Reason: {app.companyCorrectedReason}
                                            </p>
                                          )}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-foreground truncate max-w-48">{app?.company.companyLocation}</td>
                              <td className="px-4 py-4 text-sm text-foreground">
                                <span
                                  className="cursor-pointer hover:text-orange-600"
                                  title="Copy email"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    navigator.clipboard.writeText(app.candidate.email)
                                    toast.success("Email copied!")
                                  }}
                                >
                                  {app.candidate.email}
                                </span>
                              </td>
                              <td className="px-4 py-4 text-sm text-foreground">
                                <span
                                  className="cursor-pointer hover:text-orange-600"
                                  title="Copy phone number"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    navigator.clipboard.writeText(app.candidate.phone)
                                    toast.success("Phone number copied!")
                                  }}
                                >
                                  {app.candidate.phone}
                                </span>
                              </td>
                              <td className="px-4 py-4">
                                <Badge className={getStatusBadgeColor(app.currentStatus?.status)}>
                                  {app.currentStatus?.status || "N/A"}
                                </Badge>
                              </td>
                              <td className="px-4 py-4 text-sm text-foreground">{app.sourcedBy?.username || "N/A"}</td>
                              <td className="px-4 py-4 text-sm text-muted-foreground">
                                {new Date(app.appliedOn).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </td>
                              <td className="px-4 py-4 text-sm text-muted-foreground">
                                {getInterviewDate(app) || "N/A"}
                              </td>
                              <td className="px-4 py-4">
                                <Button
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleViewInterviewDetails(app)
                                    toast.dismiss()
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-2 rounded-lg px-3 py-1.5 border border-orange-300 text-orange-600 font-medium bg-white shadow-sm hover:bg-orange-50 hover:shadow-md hover:border-orange-400 active:scale-95 transition-all duration-200"
                                  disabled={app.currentStatus?.status === "Joined"}
                                >
                                  <Eye className="h-4 w-4" />
                                  View
                                </Button>
                              </td>
                              <td className="px-4 py-4">{renderActionsDropdown(app)}</td>
                            </tr>
                          ))
                          })()
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Mobile Card View */}
                  <div className="lg:hidden">
                    <div className="space-y-2 sm:space-y-3 p-2 sm:p-4">
                      {interviewsLoading ? (
                        <>
                          {[...Array(5)].map((_, index) => (
                            <div key={`mobile-shimmer-${index}`} className="bg-white rounded-lg border border-gray-200 p-4">
                              <div className="animate-pulse space-y-3">
                                <div className="flex items-center gap-3">
                                  <div className="bg-gray-200 rounded-full w-10 h-10"></div>
                                  <div className="bg-gray-200 rounded w-32 h-4"></div>
                                </div>
                                <div className="space-y-2">
                                  <div className="bg-gray-200 rounded w-full h-3"></div>
                                  <div className="bg-gray-200 rounded w-3/4 h-3"></div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      ) : interviews.length === 0 ? (
                        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                          <p className="text-muted-foreground">
                            {user?.role === "ADMIN" && selectedSHM === "all"
                              ? "Please select an SHM to view interviews"
                              : "No interviews found for your coordinated jobs."}
                          </p>
                          {user?.role === "ADMIN" && selectedSHM === "all" && (
                            <p className="text-sm text-muted-foreground mt-2">
                              Select an SHM from the dropdown above to see their interviews
                            </p>
                          )}
                        </div>
                      ) : (
                        interviews.map((app) => (
                          <div
                            key={app.applicationId}
                            className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4"
                          >
                            <div className="flex items-start justify-between mb-2 sm:mb-3">
                              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                <div className="h-8 w-8 sm:h-10 sm:w-10 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm flex-shrink-0">
                                  {app.candidate.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{app.candidate.name}</h3>
                                  <p className="text-xs sm:text-sm text-gray-500 truncate flex items-center gap-1">
                                    <span className="truncate">{app?.company.companyName}</span>
                                    {app.companyCorrected && (
                                      <Badge
                                        variant="outline"
                                        className="text-[10px] px-1 py-0 h-4 border-amber-300 bg-amber-50 text-amber-800 shrink-0"
                                        title={[
                                          "Company corrected by Admin",
                                          app.companyCorrectedAt
                                            ? format(new Date(app.companyCorrectedAt), "dd MMM yyyy")
                                            : "",
                                          app.companyCorrectedReason
                                            ? `Reason: ${app.companyCorrectedReason}`
                                            : "",
                                        ]
                                          .filter(Boolean)
                                          .join("\n")}
                                      >
                                        Changed
                                      </Badge>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <Badge className={`${getStatusBadgeColor(app.currentStatus?.status)} text-xs px-2 py-1 flex-shrink-0`}>
                                {app.currentStatus?.status || "N/A"}
                              </Badge>
                            </div>
                            
                            <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                              <div className="flex items-center gap-2 text-xs sm:text-sm">
                                <Mail className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                                <span className="text-gray-600 truncate">{app.candidate.email}</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs sm:text-sm">
                                <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                                <span className="text-gray-600">{app.candidate.phone}</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs sm:text-sm">
                                <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                                <span className="text-gray-600">
                                  Interview: {getInterviewDate(app) || "N/A"}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleViewInterviewDetails(app)
                                  toast.dismiss()
                                }}
                                variant="outline"
                                size="sm"
                                className="flex-1 flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 border border-orange-300 text-orange-600 font-medium bg-white shadow-sm hover:bg-orange-50"
                                disabled={app.currentStatus?.status === "Joined"}
                              >
                                <Eye className="h-4 w-4" />
                                View Interview
                              </Button>
                              <div className="flex items-center">
                                {renderActionsDropdown(app)}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Pagination for Interviews */}
              {interviewsTotalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3 p-2 sm:p-4 pl-2 sm:pl-8 bg-white border-t border-gray-200 flex-shrink-0">
                  <div className="text-xs sm:text-sm text-muted-foreground order-2 sm:order-1">
                    Showing {interviews.length} of {interviewsTotalResults} Interview applications
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 order-1 sm:order-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (interviewsPage > 1 && !interviewsLoading) {
                          setInterviewsPage(interviewsPage - 1)
                        }
                      }}
                      disabled={interviewsPage === 1 || interviewsLoading}
                      className="rounded-lg border-gray-200 hover:bg-orange-50 h-7 w-7 sm:h-8 sm:w-8 p-0"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: interviewsTotalPages }, (_, i) => i + 1)
                        .filter(
                          (pageNum) =>
                            pageNum === 1 ||
                            pageNum === interviewsTotalPages ||
                            (pageNum >= interviewsPage - 2 && pageNum <= interviewsPage + 2)
                        )
                        .map((pageNum, index, array) => {
                          // Add ellipsis before if there's a gap
                          const showEllipsisBefore = index > 0 && pageNum - array[index - 1] > 1
                          return (
                            <React.Fragment key={pageNum}>
                              {showEllipsisBefore && (
                                <span className="text-sm text-muted-foreground px-1">...</span>
                              )}
                              <Button
                                variant={interviewsPage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => {
                                  if (!interviewsLoading) {
                                    setInterviewsPage(pageNum)
                                  }
                                }}
                                disabled={interviewsLoading}
                                className={
                                  interviewsPage === pageNum
                                    ? "bg-[#EC6E51] text-white hover:bg-orange-700 h-7 w-7 sm:h-8 sm:w-8 p-0"
                                    : "border-gray-200 hover:bg-orange-50 h-7 w-7 sm:h-8 sm:w-8 p-0"
                                }
                              >
                                {pageNum}
                              </Button>
                            </React.Fragment>
                          )
                        })}
                      {interviewsTotalPages > 5 && interviewsPage < interviewsTotalPages - 2 && (
                        <span className="text-sm text-muted-foreground px-1">...</span>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (interviewsPage < interviewsTotalPages && !interviewsLoading) {
                          setInterviewsPage(interviewsPage + 1)
                        }
                      }}
                      disabled={interviewsPage === interviewsTotalPages || interviewsLoading}
                      className="rounded-lg border-gray-200 hover:bg-orange-50 h-7 w-7 sm:h-8 sm:w-8 p-0"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          )}
      </Tabs>

      <Dialog
        open={showEditCandidateModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowEditCandidateModal(false)
            setEditCandidateApp(null)
            setEditCandidateForm({ name: "", email: "", phone: "" })
          }
        }}
      >
        <DialogContent className="w-[95vw] max-w-md rounded-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Edit candidate
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="rounded-full border border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600"
                    >
                      i
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-left text-xs">
                    Change how this candidate appears in the system. Old and new values, your user id, and time are stored
                    in candidateEditHistory. Email and phone must stay unique across candidates.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DialogTitle>
            <DialogDescription>Edit name, email, and phone for this applicant&apos;s candidate record.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="edit-candidate-name">Name *</Label>
              <Input
                id="edit-candidate-name"
                value={editCandidateForm.name}
                onChange={(e) => {
                  setEditCandidateForm((f) => ({ ...f, name: e.target.value }))
                  setEditCandidateFieldErrors((prev) => ({ ...prev, name: "" }))
                }}
              />
              {editCandidateFieldErrors.name && (
                <p className="text-xs text-red-600">{editCandidateFieldErrors.name}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-candidate-email">Email *</Label>
              <Input
                id="edit-candidate-email"
                type="email"
                value={editCandidateForm.email}
                onChange={(e) => {
                  setEditCandidateForm((f) => ({ ...f, email: e.target.value }))
                  setEditCandidateFieldErrors((prev) => ({ ...prev, email: "" }))
                }}
              />
              {editCandidateFieldErrors.email && (
                <p className="text-xs text-red-600">{editCandidateFieldErrors.email}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-candidate-phone">Phone *</Label>
              <Input
                id="edit-candidate-phone"
                value={editCandidateForm.phone}
                inputMode="numeric"
                maxLength={10}
                onChange={(e) =>
                  {
                    setEditCandidateForm((f) => ({
                      ...f,
                      phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                    }))
                    setEditCandidateFieldErrors((prev) => ({ ...prev, phone: "" }))
                  }
                }
              />
              {editCandidateFieldErrors.phone && (
                <p className="text-xs text-red-600">{editCandidateFieldErrors.phone}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={editCandidateSaving}
              onClick={() => {
                setShowEditCandidateModal(false)
                setEditCandidateApp(null)
                setEditCandidateForm({ name: "", email: "", phone: "" })
                setEditCandidateFieldErrors({ name: "", email: "", phone: "" })
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-orange-600 hover:bg-orange-700"
              disabled={editCandidateSaving}
              onClick={async () => {
                if (!editCandidateApp?.candidate?.id) return
                const payload = {
                  name: editCandidateForm.name.trim(),
                  email: editCandidateForm.email.trim(),
                  phone: editCandidateForm.phone.trim(),
                }
                const nextErrors = {
                  name: payload.name ? "" : "Name is required.",
                  email: payload.email ? "" : "Email is required.",
                  phone: payload.phone ? "" : "Phone is required.",
                }
                setEditCandidateFieldErrors(nextErrors)
                if (nextErrors.name || nextErrors.email || nextErrors.phone) {
                  toast.error("Please fill all required fields.")
                  return
                }
                setEditCandidateSaving(true)
                try {
                  await updateCandidateContactDetails(editCandidateApp.candidate.id, payload)
                  setShowEditCandidateModal(false)
                  setEditCandidateApp(null)
                  await fetchInterviews(interviewsPage)
                  toast.success("Candidate contact updated successfully.")
                } catch (err: any) {
                  const msg =
                    err?.response?.data?.message ||
                    err?.message ||
                    "Failed to update candidate contact."
                  toast.error(msg)
                } finally {
                  setEditCandidateSaving(false)
                }
              }}
            >
              {editCandidateSaving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Interview Details Modal */}
      <InterviewDetailsModal
        isOpen={showInterviewModal}
        onClose={() => setShowInterviewModal(false)}
        selectedApplication={selectedApplication}
        interviewDetails={interviewDetails}
        setInterviewDetails={setInterviewDetails}
        showScheduleForm={showScheduleForm}
        setShowScheduleForm={setShowScheduleForm}
        scheduleFormData={scheduleFormData}
        setScheduleFormData={setScheduleFormData}
        validationErrors={validationErrors}
        setValidationErrors={setValidationErrors}
        emailInput={emailInput}
        setEmailInput={setEmailInput}
        currentRoundForAction={currentRoundForAction}
        setCurrentRoundForAction={setCurrentRoundForAction}
        user={user ? { ...user, username: user.username ?? "" } : null}
        isScheduling={isScheduling}
        onScheduleSubmit={handleSubmitSchedule}
        onResultRound={handleResultRound}
        onFetchJobLocation={fetchAndPopulateJobLocation}
        showLoader={showLoaderInInterviewPopup}
      />

      {/* Result Submission Modal */}
      <ResultModal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        resultFormData={resultFormData}
        setResultFormData={setResultFormData}
        isSubmittingResult={isSubmittingResult}
        onSubmit={handleSubmitResult}
      />

      {/* Status Update Modal */}
      <Dialog
        open={showStatusUpdateModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowStatusUpdateModal(false)
            setStatusUpdateData({ applicationId: "", newStatus: "", comments: "", selectedJobId: "", currentStatus: "" })
          }
        }}
      >
        <DialogContent className="w-[95vw] max-w-[500px] rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900">Update Application Status</DialogTitle>
            <DialogDescription className="text-sm sm:text-base text-gray-600">
              Select a new status for the application and provide any additional comments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between w-full gap-4 sm:gap-2">
              <div className={`flex flex-col w-full ${isNotSelectedOnlyStatusChange(statusUpdateData.currentStatus || "") ? "" : "sm:w-[47%]"}`}>
                <label className="text-sm font-medium text-gray-700">New Status</label>
                <SearchableSelect
                  options={getStatusChangeOptions(statusUpdateData.currentStatus || "")}
                  value={statusUpdateData.newStatus}
                  onValueChange={(value) => setStatusUpdateData((prev) => ({ ...prev, newStatus: value }))}
                  placeholder="Select status"
                  searchPlaceholder="Search status..."
                  emptyText="No status available"
                  className="w-full mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-400 h-10"
                />
              </div>
              {statusUpdateData.currentStatus && !isNotSelectedOnlyStatusChange(statusUpdateData.currentStatus) && (
              <div className="flex flex-col w-full sm:w-[47%]">
                <label
                  className={`text-sm font-medium ${statusUpdateData.newStatus !== "Joined"
                      ? "text-gray-400"
                      : "text-gray-700"
                    }`}
                >
                  {statusUpdateData.newStatus === "Joined"
                    ? "Select Joining Date"
                    : statusUpdateData.newStatus === "Offered"
                      ? "Select Offered Date"
                      : "Select Reassigned Date"}
                </label>
                <Input
                  type="date"
                  value={joiningDate}
                  onChange={(e) => setJoiningDate(e.target.value)}
                  disabled={!showDateSelect}
                  min={new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]}
                  max={new Date().toISOString().split("T")[0]}
                  className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 mt-1"
                  title="Select Joining Date"
                />
              </div>
              )}
            </div>
            {statusUpdateData.newStatus === "Reassign Company" && (
              <div>
                <label className="text-sm font-medium text-gray-700">Select New Job</label>
                <SearchableSelect
                  options={jobs
                    .filter((job) => job.status === "published")
                    .map((job) => ({
                      value: job.id,
                      label: (
                        <div className="text-left">
                          <div>{job.title}</div>
                          <div className="text-sm text-gray-500">{job?.companyName}</div>
                          <div className="text-sm text-gray-500">{job?.location}</div>
                        </div>
                      ),
                      searchText: `${job.title} ${job?.companyName} ${job?.location}`,
                    }))}
                  value={statusUpdateData.selectedJobId || ""}
                  onValueChange={(value) => setStatusUpdateData((prev) => ({ ...prev, selectedJobId: value }))}
                  placeholder="Search and select a job..."
                  searchPlaceholder="Search jobs..."
                  emptyText="No jobs available"
                  className="w-full mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 h-10"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700">Comments</label>
              <Textarea
                value={statusUpdateData.comments}
                onChange={(e) => setStatusUpdateData((prev) => ({ ...prev, comments: e.target.value }))}
                placeholder="Enter comments (optional)"
                className="w-full h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowStatusUpdateModal(false)
                  setStatusUpdateData({ applicationId: "", newStatus: "", comments: "", selectedJobId: "", currentStatus: "" })
                }}
                className="rounded-xl border-gray-200 w-full sm:w-auto order-2 sm:order-1"
                disabled={isReassigning}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (statusUpdateData.newStatus === "Reassign Company" && !statusUpdateData.selectedJobId) {
                    toast.error("Please select a job to reassign to")
                    return
                  }
                  await handleUpdateStatus(
                    statusUpdateData.applicationId,
                    statusUpdateData.newStatus,
                    statusUpdateData.comments,
                    statusUpdateData.selectedJobId,
                    joiningDate
                  )
                  setShowStatusUpdateModal(false)
                  setStatusUpdateData({ applicationId: "", newStatus: "", comments: "", selectedJobId: "", currentStatus: "" })
                }}
                disabled={!statusUpdateData.newStatus || isReassigning}
                className="rounded-xl bg-orange-600 text-white hover:bg-orange-700 w-full sm:w-auto order-1 sm:order-2"
              >
                {isReassigning ? "Processing..." : "Update Status"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Joining Date Modal */}
      <Dialog
        open={showJoiningDateUpdateModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowJoiningDateUpdateModal(false)
          }
        }}
      >
        <DialogContent className="w-[95vw] max-w-[500px] rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900">Update Joining Date</DialogTitle>
            <DialogDescription className="text-sm sm:text-base text-gray-600">
              Update the joining date for the application and provide any additional comments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between w-full gap-4 sm:gap-2">
              <div className="flex flex-col w-full sm:w-[47%]">
                <label className="text-sm font-medium text-gray-700">New Status</label>
                <SearchableSelect
                  options={[
                    { value: "Joined", label: "Joined" },
                    { value: "Offered", label: "Offered" },
                    { value: "Reassign Company", label: "Reassign Company" },
                  ]}
                  disabled
                  value={statusUpdateData.newStatus}
                  onValueChange={(value) => setStatusUpdateData((prev) => ({ ...prev, newStatus: value }))}
                  placeholder="Select status"
                  searchPlaceholder="Search status..."
                  emptyText="No status available"
                  className="w-full mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-400 h-10"
                />
              </div>
              <div className="flex flex-col w-full sm:w-[47%]">
                <label className="text-sm font-medium text-gray-700">
                  Select Joining Date
                </label>
                <Input
                  type="date"
                  value={joiningDate}
                  onChange={(e) => setJoiningDate(e.target.value)}
                  max={new Date().toLocaleDateString("en-CA")}
                  className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 mt-1"
                  title="Select Joining Date"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Comments</label>
              <Textarea
                value={statusUpdateData.comments}
                onChange={(e) => setStatusUpdateData((prev) => ({ ...prev, comments: e.target.value }))}
                placeholder="Enter comments (optional)"
                className="w-full h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowJoiningDateUpdateModal(false)
                }}
                className="rounded-xl border-gray-200 w-full sm:w-auto order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateJoiningDate}
                className="rounded-xl bg-orange-600 text-white hover:bg-orange-700 w-full sm:w-auto order-1 sm:order-2"
              >
                Update Joining Date
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showChangeCompanyModal}
        onOpenChange={(open) => {
          if (!open && !isChangingCompany) {
            setShowChangeCompanyModal(false)
            setChangeCompanyData({
              applicationId: "",
              selectedJobId: "",
              selectedJobLabel: "",
              comments: "",
              candidateName: "",
              currentCompany: "",
            })
          }
        }}
      >
        <DialogContent
          className="w-[95vw] max-w-md rounded-2xl bg-white"
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement | null
            if (
              target?.closest("[data-radix-popper-content-wrapper]") ||
              target?.closest("[cmdk-root]") ||
              target?.closest("[data-radix-select-content]")
            ) {
              e.preventDefault()
            }
          }}
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement | null
            if (
              target?.closest("[data-radix-popper-content-wrapper]") ||
              target?.closest("[cmdk-root]") ||
              target?.closest("[data-radix-select-content]")
            ) {
              e.preventDefault()
            }
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault()
            document.body.style.removeProperty("pointer-events")
            document.body.removeAttribute("data-scroll-locked")
          }}
        >
          <DialogHeader>
            <DialogTitle>Change Company</DialogTitle>
            <DialogDescription>
              Correct the job/company on the same application for{" "}
              <span className="font-medium text-foreground">{changeCompanyData.candidateName || "this candidate"}</span>
              {changeCompanyData.currentCompany ? (
                <>
                  {" "}
                  (currently <span className="font-medium text-foreground">{changeCompanyData.currentCompany}</span>).
                </>
              ) : (
                "."
              )}{" "}
              Status will not change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Select New Job</Label>
              <SearchableSelect
                options={(() => {
                  const options = changeCompanyJobs
                    .filter((job) => !job.status || String(job.status).toLowerCase() === "published")
                    .map((job) => {
                      const id = String(job.id || job._id || "")
                      const title = job.title || "Untitled"
                      const company = job?.companyName || "N/A"
                      const location = job?.location || ""
                      return {
                        value: id,
                        triggerLabel: location
                          ? `${title} · ${company} · ${location}`
                          : `${title} · ${company}`,
                        label: (
                          <div className="text-left space-y-0.5">
                            <div className="font-medium text-sm text-foreground leading-tight">{title}</div>
                            <div className="text-xs text-muted-foreground leading-tight">
                              {[company, location].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        ),
                        searchText: `${title} ${company} ${location}`,
                      }
                    })
                    .filter((o) => o.value)
                  if (
                    changeCompanyData.selectedJobId &&
                    !options.some((o) => o.value === changeCompanyData.selectedJobId) &&
                    changeCompanyData.selectedJobLabel
                  ) {
                    options.unshift({
                      value: changeCompanyData.selectedJobId,
                      triggerLabel: changeCompanyData.selectedJobLabel,
                      label: (
                        <div className="text-left">
                          <div className="font-medium text-sm">{changeCompanyData.selectedJobLabel}</div>
                        </div>
                      ),
                      searchText: changeCompanyData.selectedJobLabel,
                    })
                  }
                  return options
                })()}
                value={changeCompanyData.selectedJobId}
                onValueChange={(value) => {
                  const job = changeCompanyJobs.find((j) => String(j.id || j._id || "") === value)
                  const title = job?.title || "Untitled"
                  const company = job?.companyName || "N/A"
                  const location = job?.location || ""
                  setChangeCompanyData((prev) => ({
                    ...prev,
                    selectedJobId: value,
                    selectedJobLabel: job
                      ? location
                        ? `${title} · ${company} · ${location}`
                        : `${title} · ${company}`
                      : prev.selectedJobLabel,
                  }))
                }}
                onSearchChange={async (value: string) => {
                  setIsLoadingChangeCompanyJobs(true)
                  try {
                    const response = await getJobs({
                      status: "published",
                      limit: 50,
                      page: 1,
                      search: value || undefined,
                    })
                    setChangeCompanyJobs(response?.data?.jobs || [])
                  } catch {
                    setChangeCompanyJobs([])
                  } finally {
                    setIsLoadingChangeCompanyJobs(false)
                  }
                }}
                placeholder="Search and select a job..."
                searchPlaceholder="Search jobs..."
                emptyText="No jobs available"
                className="w-full mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 h-10"
                loading={isLoadingChangeCompanyJobs && !changeCompanyData.selectedJobId}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">
                Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={changeCompanyData.comments}
                onChange={(e) =>
                  setChangeCompanyData((prev) => ({ ...prev, comments: e.target.value }))
                }
                placeholder="Why is the company being corrected?"
                className="w-full mt-1 h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-400"
                disabled={isChangingCompany}
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
              <Button
                variant="outline"
                disabled={isChangingCompany}
                onClick={() => setShowChangeCompanyModal(false)}
                className="rounded-xl border-gray-200 w-full sm:w-auto order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button
                disabled={
                  isChangingCompany ||
                  !changeCompanyData.selectedJobId ||
                  !changeCompanyData.comments.trim()
                }
                onClick={async () => {
                  if (!changeCompanyData.applicationId || !changeCompanyData.selectedJobId) return
                  const reason = changeCompanyData.comments.trim()
                  if (!reason) {
                    toast.error("Reason is required")
                    return
                  }
                  setIsChangingCompany(true)
                  try {
                    await editApplication(changeCompanyData.applicationId, {
                      jobId: changeCompanyData.selectedJobId,
                      jobType: "main",
                      comments: reason,
                    })
                    setShowChangeCompanyModal(false)
                    setChangeCompanyData({
                      applicationId: "",
                      selectedJobId: "",
                      selectedJobLabel: "",
                      comments: "",
                      candidateName: "",
                      currentCompany: "",
                    })
                    await fetchInterviews(interviewsPage)
                  } catch {
                    // toast handled in editApplication
                  } finally {
                    setIsChangingCompany(false)
                  }
                }}
                className="rounded-xl bg-orange-600 text-white hover:bg-orange-700 w-full sm:w-auto order-1 sm:order-2"
              >
                {isChangingCompany ? "Saving..." : "Change Company"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  )
}

export default CoordinationsPage

